import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { AlertCircle, Calendar, CheckCircle2, Clock, GripVertical, Pencil, Trash2, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type { ContractPipelineCard, ContratoPipelineFase } from "@/services/locacoes";
import { formatDate, formatNumber } from "@/utils/format";
import {
  CONTRATO_PIPELINE_FASE_COLORS,
  CONTRATO_PIPELINE_FASE_LABELS,
} from "./locacoesTypes";

const PIPELINE_ORDER: ContratoPipelineFase[] = [
  "setup",
  "vigente",
  "proximo_vencimento",
  "encerrado",
];

/** Coluna removida da UI; contratos ainda nessa fase aparecem em Setup. */
function displayFase(fase: ContratoPipelineFase): ContratoPipelineFase {
  if (fase === "captacao") return "setup";
  return PIPELINE_ORDER.includes(fase) ? fase : "setup";
}

function sortCardsInColumn(a: ContractPipelineCard, b: ContractPipelineCard): number {
  const codA = a.codigoContrato || "";
  const codB = b.codigoContrato || "";
  const byCodigo = codB.localeCompare(codA, "pt-BR", { numeric: true });
  if (byCodigo !== 0) return byCodigo;
  return a.id.localeCompare(b.id);
}

function groupCardsByFase(cards: ContractPipelineCard[]): Record<ContratoPipelineFase, ContractPipelineCard[]> {
  const map: Record<ContratoPipelineFase, ContractPipelineCard[]> = {
    captacao: [],
    setup: [],
    vigente: [],
    proximo_vencimento: [],
    encerrado: [],
  };
  for (const c of cards) {
    map[displayFase(c.pipelineFase)].push(c);
  }
  for (const fase of PIPELINE_ORDER) {
    map[fase].sort(sortCardsInColumn);
  }
  return map;
}

function flattenColumns(map: Record<ContratoPipelineFase, ContractPipelineCard[]>): ContractPipelineCard[] {
  return PIPELINE_ORDER.flatMap((fase) => map[fase]);
}

interface Props {
  cards: ContractPipelineCard[];
  busy?: boolean;
  onMove: (contratoId: string, fase: ContratoPipelineFase) => Promise<void>;
  onView: (card: ContractPipelineCard) => void;
  onEdit?: (card: ContractPipelineCard) => void;
  onDelete?: (card: ContractPipelineCard) => void;
}

function SaudeBadge({ saude }: { saude: ContractPipelineCard["saudeApontamentos"] }) {
  if (saude === "sem_dados") return null;
  const ok = saude === "ok";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold",
        ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
      )}
      title={ok ? "Apontamentos em dia" : "Apontamentos atrasados"}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {ok ? "Apont. OK" : "Apont. atrasado"}
    </span>
  );
}

function KanbanCard({
  card,
  index,
  onView,
  onEdit,
  onDelete,
}: {
  card: ContractPipelineCard;
  index: number;
  onView: (c: ContractPipelineCard) => void;
  onEdit?: (c: ContractPipelineCard) => void;
  onDelete?: (c: ContractPipelineCard) => void;
}) {
  return (
    <Draggable draggableId={card.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={cn(
            "shrink-0 rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm transition-shadow sm:p-3",
            snapshot.isDragging && "shadow-lg ring-2 ring-brand-200",
          )}
        >
          <div className="mb-2 flex items-start gap-1">
            <span {...provided.dragHandleProps} className="mt-0.5 cursor-grab text-slate-300 hover:text-slate-500">
              <GripVertical className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-800">
                {card.codigoContrato ? `${card.codigoContrato} — ` : ""}
                {card.clienteSiengeNome || "—"}
              </p>
              <p className="truncate text-[11px] text-slate-500">{card.modeloContrato}</p>
              {card.infoBasicos?.trim() ? (
                <p
                  className="mt-1 line-clamp-3 text-[11px] leading-snug text-slate-600"
                  title={card.infoBasicos.trim()}
                >
                  {card.infoBasicos.trim()}
                </p>
              ) : null}
            </div>
          </div>
          <p className="mb-2 text-sm font-semibold tabular-nums text-brand-700">
            {card.porHoras
              ? `R$ ${formatNumber(card.valorPeriodo ?? 0, 2)} (${formatNumber(card.horasPeriodo ?? 0, 1)} h)`
              : card.valorContrato > 0
                ? `R$ ${formatNumber(card.valorContrato, 2)}`
                : card.valorPotencial
                  ? `Pot. R$ ${formatNumber(card.valorPotencial, 2)}`
                  : "—"}
          </p>
          {card.maquinaNomes.length > 0 && (
            <p className="mb-2 flex items-center gap-1 text-xs text-slate-600">
              <Truck className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {card.maquinaNomes.length <= 2
                  ? card.maquinaNomes.join(", ")
                  : `${card.maquinaNomes.slice(0, 2).join(", ")} +${card.maquinaNomes.length - 2}`}
              </span>
            </p>
          )}
          <div className="mb-2 flex flex-wrap gap-1">
            <SaudeBadge saude={card.saudeApontamentos} />
            {card.operadoresCount > 0 && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                {card.operadoresCount} op.
              </span>
            )}
            {(card.registrosCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
                <Clock className="h-3 w-3" />
                {card.registrosCount} reg.
              </span>
            )}
          </div>
          {card.pendencias.length > 0 && (
            <div className="mb-2 flex items-start gap-1 text-[10px] text-amber-700">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{card.pendencias[0]}</span>
            </div>
          )}
          {card.dataFimPrevista && (
            <p className="mb-2 flex items-center gap-1 text-[10px] text-slate-400">
              <Calendar className="h-3 w-3" />
              Término {formatDate(card.dataFimPrevista)}
            </p>
          )}
          <div className="flex flex-wrap gap-1">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="min-w-0 flex-1"
              onClick={() => onView(card)}
            >
              Ver detalhes
            </Button>
            {onEdit && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEdit(card)}
                leftIcon={<Pencil className="h-3.5 w-3.5" />}
                title="Editar contrato"
              >
                Editar
              </Button>
            )}
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDelete(card)}
                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                title="Excluir contrato"
              >
                Excluir
              </Button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

export function ContratoKanbanBoard({ cards, busy, onMove, onView, onEdit, onDelete }: Props) {
  const [localCards, setLocalCards] = useState<ContractPipelineCard[] | null>(null);
  const displayCards = localCards ?? cards;

  const byFase = useMemo(() => groupCardsByFase(displayCards), [displayCards]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const destFase = result.destination.droppableId as ContratoPipelineFase;
    const sourceFase = result.source.droppableId as ContratoPipelineFase;
    const contratoId = result.draggableId;
    if (sourceFase === destFase && result.source.index === result.destination.index) return;

    const prev = displayCards;
    const columns = groupCardsByFase(displayCards);
    const sourceCol = columns[sourceFase];
    const [moved] = sourceCol.splice(result.source.index, 1);
    if (!moved || moved.id !== contratoId) return;

    moved.pipelineFase = destFase;
    columns[destFase].splice(result.destination.index, 0, moved);
    const next = flattenColumns(columns);
    setLocalCards(next);
    try {
      await onMove(contratoId, destFase);
      setLocalCards(null);
    } catch {
      setLocalCards(prev);
    }
  };

  return (
    <DragDropContext onDragEnd={(r) => void handleDragEnd(r)}>
      <div className="w-full min-w-0">
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {PIPELINE_ORDER.map((fase) => {
            const col = byFase[fase];
            return (
              <div key={fase} className="flex min-w-0 flex-col">
                <div
                  className={cn(
                    "mb-2 flex shrink-0 items-start justify-between gap-2 rounded-lg border px-3 py-2",
                    CONTRATO_PIPELINE_FASE_COLORS[fase],
                  )}
                >
                  <p className="min-w-0 text-[11px] font-bold uppercase leading-snug tracking-wide text-slate-700 sm:text-xs">
                    {CONTRATO_PIPELINE_FASE_LABELS[fase]}
                  </p>
                  <span className="shrink-0 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-slate-600">
                    {col.length}
                  </span>
                </div>
                <Droppable droppableId={fase}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={cn(
                        "min-h-[7rem] flex-1 space-y-2 overflow-y-auto rounded-lg border border-dashed p-2 transition-colors",
                        "max-h-[min(42vh,18rem)] sm:max-h-[min(48vh,22rem)] xl:max-h-[min(68vh,34rem)]",
                        "[scrollbar-gutter:stable]",
                        snapshot.isDraggingOver ? "border-brand-300 bg-brand-50/50" : "border-slate-200 bg-slate-50/50",
                        busy && "pointer-events-none opacity-60",
                      )}
                    >
                      {col.map((card, index) => (
                        <KanbanCard
                          key={card.id}
                          card={card}
                          index={index}
                          onView={onView}
                          onEdit={onEdit}
                          onDelete={onDelete}
                        />
                      ))}
                      {provided.placeholder}
                      {col.length === 0 && (
                        <p className="py-6 text-center text-xs text-slate-400">Arraste contratos para cá</p>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </div>
    </DragDropContext>
  );
}
